// i18n — EN / SQ / IT / DE. Persists to localStorage. Falls back to EN.
import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';

const I18nContext = createContext(null);

export const LANGUAGES = [
  { code: 'en', label: 'English',   flag: '🇬🇧' },
  { code: 'sq', label: 'Shqip',     flag: '🇦🇱' },
  { code: 'it', label: 'Italiano',  flag: '🇮🇹' },
  { code: 'de', label: 'Deutsch',   flag: '🇩🇪' },
];

const EN = {
  'nav.browse': 'Browse',
  'nav.plan_trip': 'Plan trip',
  'nav.map': 'Map',
  'nav.events': 'Events',
  'nav.ask_local': 'Ask a Local',
  'nav.emergency': 'Emergency',
  'nav.guides': 'Guides',
  'nav.for_business': 'For Business',
  'nav.login': 'Login',
  'nav.register': 'Register',
  'nav.claim': 'Claim',
  'nav.dashboard': 'Dashboard',
  'nav.favorites': 'Favorites',
  'nav.passport': 'My Passport',
  'nav.recommendations': 'For you',
  'nav.logout': 'Sign out',
  'common.search': 'Search',
  'common.view_all': 'View all',
  'common.view_details': 'View details',
  'common.loading': 'Loading…',
  'common.save': 'Save',
  'common.saved': 'Saved',
  'common.share': 'Share',
  'common.book_now': 'Book now',
  'common.whatsapp': 'WhatsApp',
  'common.phone': 'Call',
  'common.website': 'Website',
  'common.directions': 'Directions',
  'common.reviews': 'Reviews',
  'common.amenities': 'Amenities',
  'common.cuisine': 'Cuisine',
  'common.cancel': 'Cancel',
  'common.submit': 'Submit',
  'common.signin_required': 'Sign in required',
  'home.hero_title': "Discover Albania's Hidden Gems",
  'category.best_in': 'Best {plural} in {city}',
  'category.results_count': '{count} results',
  'dashboard.welcome': 'Welcome to your business dashboard',
  'dashboard.no_listings': 'You have not claimed any listings yet.',
  'dashboard.find_listing': 'Find your business',
  'dashboard.claim_cta': 'Claim a listing',
  'dashboard.tabs.overview': 'Overview',
  'dashboard.tabs.listings': 'Listings',
  'dashboard.tabs.leads': 'Leads',
  'dashboard.tabs.reviews': 'Reviews',
  'dashboard.tabs.analytics': 'Analytics',
  'dashboard.tabs.billing': 'Billing',
  'dashboard.tabs.settings': 'Settings',
  'dashboard.views': 'Views',
  'dashboard.whatsapp_clicks': 'WhatsApp clicks',
  'dashboard.website_clicks': 'Website clicks',
  'dashboard.saves': 'Saved by users',
  'dashboard.boost_cta': 'Boost this listing',
  'dashboard.urgency_spots': 'Only {n} featured spots left in {city}',
  'events.title': 'Events in Albania',
  'events.upcoming': 'Upcoming',
  'events.ongoing': 'Ongoing',
  'events.past': 'Past',
  'events.all_cities': 'All cities',
  'events.empty': 'No events match your filters.',
  'emergency.title': 'Emergency & Safety',
  'emergency.subtitle': 'Save this page. Everything you need in a crisis — one tap away.',
  'emergency.national_numbers': 'National emergency numbers',
  'passport.title': 'InfoPoint Passport',
  'passport.share_profile': 'Share profile',
  'passport.cities_visited': 'Cities visited',
  'passport.recent_activity': 'Recent activity',
  'dna.title': 'Tourist DNA Match',
  'dna.subtitle': 'Tell us what you love — get personalized places in seconds.',
  'ask.title': 'Ask a Local',
  'ask.subtitle': 'Real answers from verified locals (3+ check-ins in the city).',
  'weather.today': 'Today',
  'weather.wind': 'Wind',
  'weather.uv': 'UV',
  'weather.humidity': 'Humidity',
  'checkin.cta': 'Check in',
};

const SQ = {
  'nav.browse': 'Eksploro',
  'nav.plan_trip': 'Plano udhëtim',
  'nav.map': 'Harta',
  'nav.events': 'Eventet',
  'nav.ask_local': 'Pyet një Vendas',
  'nav.emergency': 'Emergjencë',
  'nav.guides': 'Udhëzues',
  'nav.for_business': 'Për Biznese',
  'nav.login': 'Hyr',
  'nav.register': 'Regjistrohu',
  'nav.claim': 'Pretendo',
  'nav.dashboard': 'Paneli',
  'nav.favorites': 'Të preferuarat',
  'nav.passport': 'Pasaporta ime',
  'nav.recommendations': 'Për ty',
  'nav.logout': 'Dil',
  'common.search': 'Kërko',
  'common.view_all': 'Shiko të gjitha',
  'common.view_details': 'Shiko detajet',
  'common.loading': 'Po ngarkohet…',
  'common.save': 'Ruaj',
  'common.saved': 'I ruajtur',
  'common.share': 'Shpërnda',
  'common.book_now': 'Rezervo tani',
  'common.whatsapp': 'WhatsApp',
  'common.phone': 'Telefon',
  'common.website': 'Web',
  'common.directions': 'Drejtimet',
  'common.reviews': 'Vlerësime',
  'common.amenities': 'Komoditetet',
  'common.cuisine': 'Kuzhina',
  'common.cancel': 'Anulo',
  'common.submit': 'Dërgo',
  'common.signin_required': 'Kërkohet hyrja',
  'home.hero_title': 'Zbulo Thesaret e Fshehura të Shqipërisë',
  'category.best_in': '{plural} më të mira në {city}',
  'category.results_count': '{count} rezultate',
  'dashboard.welcome': 'Mirë se erdhe në panelin tënd të biznesit',
  'dashboard.no_listings': 'Nuk ke pretenduar ende asnjë listim.',
  'dashboard.find_listing': 'Gjej biznesin tënd',
  'dashboard.claim_cta': 'Pretendo një listim',
  'dashboard.tabs.overview': 'Përmbledhje',
  'dashboard.tabs.listings': 'Listimet',
  'dashboard.tabs.leads': 'Klientët',
  'dashboard.tabs.reviews': 'Vlerësimet',
  'dashboard.tabs.analytics': 'Statistika',
  'dashboard.tabs.billing': 'Pagesat',
  'dashboard.tabs.settings': 'Cilësimet',
  'dashboard.views': 'Shikime',
  'dashboard.whatsapp_clicks': 'Klikime WhatsApp',
  'dashboard.website_clicks': 'Klikime në web',
  'dashboard.saves': 'I ruajtur nga përdoruesit',
  'dashboard.boost_cta': 'Promovo këtë listim',
  'dashboard.urgency_spots': 'Vetëm {n} vende të zgjedhura janë liruar në {city}',
  'events.title': 'Eventet në Shqipëri',
  'events.upcoming': 'Së shpejti',
  'events.ongoing': 'Në zhvillim',
  'events.past': 'Të kaluara',
  'events.all_cities': 'Të gjitha qytetet',
  'events.empty': 'Asnjë event nuk përputhet me filtrat.',
  'emergency.title': 'Emergjenca & Siguria',
  'emergency.subtitle': 'Ruaj këtë faqe. Gjithçka që ju nevojitet në krizë — një klikim larg.',
  'emergency.national_numbers': 'Numrat kombëtarë të emergjencës',
  'passport.title': 'Pasaporta InfoPoint',
  'passport.share_profile': 'Shpërnda profilin',
  'passport.cities_visited': 'Qytete të vizituara',
  'passport.recent_activity': 'Aktiviteti i fundit',
  'dna.title': 'Tourist DNA Match',
  'dna.subtitle': 'Na thuaj çfarë të pëlqen — merr vende të personalizuara në sekonda.',
  'ask.title': 'Pyet një Vendas',
  'ask.subtitle': 'Përgjigje reale nga vendasit e verifikuar (3+ check-in në qytet).',
  'weather.today': 'Sot',
  'weather.wind': 'Era',
  'weather.uv': 'UV',
  'weather.humidity': 'Lagështia',
  'checkin.cta': 'Bëj check-in',
};

const IT = {
  'nav.browse': 'Esplora',
  'nav.plan_trip': 'Pianifica viaggio',
  'nav.map': 'Mappa',
  'nav.events': 'Eventi',
  'nav.ask_local': 'Chiedi a un locale',
  'nav.emergency': 'Emergenza',
  'nav.guides': 'Guide',
  'nav.for_business': 'Per Aziende',
  'nav.login': 'Accedi',
  'nav.register': 'Registrati',
  'nav.claim': 'Rivendica',
  'nav.dashboard': 'Dashboard',
  'nav.favorites': 'Preferiti',
  'nav.passport': 'Il mio Passaporto',
  'nav.recommendations': 'Per te',
  'nav.logout': 'Esci',
  'common.search': 'Cerca',
  'common.view_all': 'Vedi tutto',
  'common.view_details': 'Vedi dettagli',
  'common.loading': 'Caricamento…',
  'common.save': 'Salva',
  'common.saved': 'Salvato',
  'common.share': 'Condividi',
  'common.book_now': 'Prenota ora',
  'common.whatsapp': 'WhatsApp',
  'common.phone': 'Chiama',
  'common.website': 'Sito',
  'common.directions': 'Indicazioni',
  'common.reviews': 'Recensioni',
  'common.amenities': 'Servizi',
  'common.cuisine': 'Cucina',
  'common.cancel': 'Annulla',
  'common.submit': 'Invia',
  'common.signin_required': 'Accesso richiesto',
  'home.hero_title': "Scopri le Gemme Nascoste dell'Albania",
  'category.best_in': 'Migliori {plural} a {city}',
  'category.results_count': '{count} risultati',
  'dashboard.welcome': 'Benvenuto nella tua dashboard aziendale',
  'dashboard.no_listings': 'Non hai ancora rivendicato annunci.',
  'dashboard.find_listing': 'Trova la tua attività',
  'dashboard.claim_cta': 'Rivendica un annuncio',
  'dashboard.tabs.overview': 'Panoramica',
  'dashboard.tabs.listings': 'Annunci',
  'dashboard.tabs.leads': 'Contatti',
  'dashboard.tabs.reviews': 'Recensioni',
  'dashboard.tabs.analytics': 'Analytics',
  'dashboard.tabs.billing': 'Fatturazione',
  'dashboard.tabs.settings': 'Impostazioni',
  'dashboard.views': 'Visualizzazioni',
  'dashboard.whatsapp_clicks': 'Click WhatsApp',
  'dashboard.website_clicks': 'Click sito web',
  'dashboard.saves': 'Salvato dagli utenti',
  'dashboard.boost_cta': 'Promuovi questo annuncio',
  'dashboard.urgency_spots': 'Solo {n} posti in evidenza rimasti a {city}',
  'events.title': 'Eventi in Albania',
  'events.upcoming': 'Prossimi',
  'events.ongoing': 'In corso',
  'events.past': 'Passati',
  'events.all_cities': 'Tutte le città',
  'events.empty': 'Nessun evento corrisponde ai filtri.',
  'emergency.title': 'Emergenza & Sicurezza',
  'emergency.subtitle': 'Salva questa pagina. Tutto ciò che serve in emergenza — un tocco via.',
  'emergency.national_numbers': 'Numeri nazionali di emergenza',
  'passport.title': 'Passaporto InfoPoint',
  'passport.share_profile': 'Condividi profilo',
  'passport.cities_visited': 'Città visitate',
  'passport.recent_activity': 'Attività recente',
  'dna.title': 'Tourist DNA Match',
  'dna.subtitle': 'Dicci cosa ami — ricevi luoghi personalizzati in secondi.',
  'ask.title': 'Chiedi a un locale',
  'ask.subtitle': 'Risposte reali da locali verificati (3+ check-in in città).',
  'weather.today': 'Oggi',
  'weather.wind': 'Vento',
  'weather.uv': 'UV',
  'weather.humidity': 'Umidità',
  'checkin.cta': 'Fai check-in',
};

const DE = {
  'nav.browse': 'Entdecken',
  'nav.plan_trip': 'Reise planen',
  'nav.map': 'Karte',
  'nav.events': 'Veranstaltungen',
  'nav.ask_local': 'Frag einen Einheimischen',
  'nav.emergency': 'Notfall',
  'nav.guides': 'Reiseführer',
  'nav.for_business': 'Für Unternehmen',
  'nav.login': 'Anmelden',
  'nav.register': 'Registrieren',
  'nav.claim': 'Beanspruchen',
  'nav.dashboard': 'Dashboard',
  'nav.favorites': 'Favoriten',
  'nav.passport': 'Mein Reisepass',
  'nav.recommendations': 'Für dich',
  'nav.logout': 'Abmelden',
  'common.search': 'Suchen',
  'common.view_all': 'Alle ansehen',
  'common.view_details': 'Details ansehen',
  'common.loading': 'Lädt…',
  'common.save': 'Speichern',
  'common.saved': 'Gespeichert',
  'common.share': 'Teilen',
  'common.book_now': 'Jetzt buchen',
  'common.whatsapp': 'WhatsApp',
  'common.phone': 'Anrufen',
  'common.website': 'Website',
  'common.directions': 'Wegbeschreibung',
  'common.reviews': 'Bewertungen',
  'common.amenities': 'Ausstattung',
  'common.cuisine': 'Küche',
  'common.cancel': 'Abbrechen',
  'common.submit': 'Absenden',
  'common.signin_required': 'Anmeldung erforderlich',
  'home.hero_title': 'Entdecke Albaniens versteckte Schätze',
  'category.best_in': 'Beste {plural} in {city}',
  'category.results_count': '{count} Ergebnisse',
  'dashboard.welcome': 'Willkommen in deinem Unternehmens-Dashboard',
  'dashboard.no_listings': 'Du hast noch keine Einträge beansprucht.',
  'dashboard.find_listing': 'Finde dein Unternehmen',
  'dashboard.claim_cta': 'Eintrag beanspruchen',
  'dashboard.tabs.overview': 'Übersicht',
  'dashboard.tabs.listings': 'Einträge',
  'dashboard.tabs.leads': 'Kunden',
  'dashboard.tabs.reviews': 'Bewertungen',
  'dashboard.tabs.analytics': 'Analysen',
  'dashboard.tabs.billing': 'Abrechnung',
  'dashboard.tabs.settings': 'Einstellungen',
  'dashboard.views': 'Aufrufe',
  'dashboard.whatsapp_clicks': 'WhatsApp-Klicks',
  'dashboard.website_clicks': 'Website-Klicks',
  'dashboard.saves': 'Gespeichert von Nutzern',
  'dashboard.boost_cta': 'Diesen Eintrag hervorheben',
  'dashboard.urgency_spots': 'Nur {n} Premium-Plätze in {city} verfügbar',
  'events.title': 'Veranstaltungen in Albanien',
  'events.upcoming': 'Bevorstehend',
  'events.ongoing': 'Laufend',
  'events.past': 'Vergangen',
  'events.all_cities': 'Alle Städte',
  'events.empty': 'Keine Veranstaltungen entsprechen deinen Filtern.',
  'emergency.title': 'Notfall & Sicherheit',
  'emergency.subtitle': 'Speichere diese Seite. Alles, was du im Notfall brauchst — ein Klick entfernt.',
  'emergency.national_numbers': 'Nationale Notrufnummern',
  'passport.title': 'InfoPoint Reisepass',
  'passport.share_profile': 'Profil teilen',
  'passport.cities_visited': 'Besuchte Städte',
  'passport.recent_activity': 'Letzte Aktivität',
  'dna.title': 'Tourist DNA Match',
  'dna.subtitle': 'Sag uns, was du liebst — erhalte personalisierte Orte in Sekunden.',
  'ask.title': 'Frag einen Einheimischen',
  'ask.subtitle': 'Echte Antworten von verifizierten Einheimischen (3+ Check-ins in der Stadt).',
  'weather.today': 'Heute',
  'weather.wind': 'Wind',
  'weather.uv': 'UV',
  'weather.humidity': 'Luftfeuchte',
  'checkin.cta': 'Einchecken',
};

const DICTS = { en: EN, sq: SQ, it: IT, de: DE };

const format = (template, params = {}) => Object.entries(params).reduce((s, [k, v]) => s.replace(new RegExp(`\\{${k}\\}`, 'g'), v), template);

export const I18nProvider = ({ children }) => {
  const [lang, setLang] = useState(() => {
    try { return localStorage.getItem('ipa_lang') || 'en'; } catch { return 'en'; }
  });
  useEffect(() => {
    try { localStorage.setItem('ipa_lang', lang); } catch (_) { /* ignore */ }
    if (typeof document !== 'undefined') document.documentElement.lang = lang;
  }, [lang]);
  const t = useCallback((key, params) => {
    const dict = DICTS[lang] || DICTS.en;
    const raw = dict[key] || DICTS.en[key] || key;
    return params ? format(raw, params) : raw;
  }, [lang]);
  return <I18nContext.Provider value={{ lang, setLang, t, languages: LANGUAGES }}>{children}</I18nContext.Provider>;
};

export const useI18n = () => useContext(I18nContext) || { lang: 'en', setLang: () => {}, t: (k) => k, languages: LANGUAGES };
